import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Button, Divider, Typography, showToast } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import {
  ProviderOverviewPage,
  useOverviewPageContext,
} from 'pages/settings/pages/billing/pages/overview/context/overviewContext'
import { OverviewPageStore } from 'pages/settings/pages/billing/pages/overview/store/overviewPageStore'
import { CreditsInfo } from 'pages/settings/pages/billing/pages/overview/ui/CreditsInfo/CreditsInfo'
import { ManageInfo } from 'pages/settings/pages/billing/pages/overview/ui/ManageInfo/ManageInfo'
import { PaymentMethods } from 'pages/settings/pages/billing/pages/overview/ui/PaymentMethods/PaymentMethods'
import { PaymentHistory } from 'pages/settings/pages/billing/pages/overview/ui/PaymentHistory/PaymentHistory'
import { PlanInfo } from 'pages/settings/pages/billing/pages/overview/ui/PlanInfo/PlanInfo'
import { FooterSection } from 'pages/settings/pages/billing/pages/overview/ui/FooterSection/FooterSection'
import styles from './styles.module.scss'

export const OverviewPageContent = observer(() => {
  const store = useOverviewPageContext()

  return (
    <div className={styles.overviewPage}>
      <PlanInfo />
      <Divider sx={{ marginTop: '40px', borderColor: 'var(--border-neutral-default)' }} />
      <CreditsInfo />
      <Divider sx={{ margin: '56px 0 62px', borderColor: 'var(--border-neutral-default)' }} />
      <ManageInfo />
      <Divider sx={{ margin: '60px 0 40px', borderColor: 'var(--border-neutral-default)' }} />
      <PaymentMethods />
      <PaymentHistory />
      <div className={styles.cancelSection}>
        <Button
          id={store.isCustomPlanOrContract ? 'custom_plan_cancel' : undefined}
          text={store.canKeepPlan ? 'Keep my plan' : 'Cancel subscription'}
          intent={store.canKeepPlan ? 'default' : 'destructive'}
          contained={store.canKeepPlan ? 'primary' : 'secondary'}
          onClick={store.canKeepPlan ? store.onKeepPlanClick : store.handleCancelSubscription}
        />
        {store.canKeepPlan && (
          <Typography ariaLabel='renewal_date' variant='body-md-regular' color='tertiary'>
            Your subscription will be canceled on {store.renewalDate}.
          </Typography>
        )}
      </div>
      <Divider sx={{ marginTop: '40px', borderColor: 'var(--border-neutral-default)' }} />
      <FooterSection />
    </div>
  )
})

type IOverviewPageLocationState = {
  checkOpportunity?: boolean
}

export const OverviewPage = observer(() => {
  const entities = useEntitiesContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [store] = useState(() => new OverviewPageStore(entities))

  useEffect(() => {
    const state = location.state as IOverviewPageLocationState | null

    if (state?.checkOpportunity) {
      store.init(true)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.key])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const setupIntent = params.get('setup_intent')
    const redirectStatus = params.get('redirect_status')
    const addCredits = params.get('add-credits')

    if (setupIntent) {
      navigate(location.pathname, { replace: true, state: location.state })
      if (redirectStatus === 'succeeded') {
        showToast({ type: 'success', title: 'ACH added' })
        entities.billingStore.fetchBillingSources()
      } else if (redirectStatus === 'failed') {
        showToast({ type: 'error', title: 'Bank account connection failed. Please try again.' })
      }
    }

    if (addCredits === 'true') {
      navigate(location.pathname, { replace: true, state: location.state })
      store.openBuyCreditsModal()
    }
  }, [])

  return (
    <ProviderOverviewPage value={store}>
      <OverviewPageContent />
    </ProviderOverviewPage>
  )
})
